import {VersionName} from '@github-ui/copilot-chat/components/VersionName'
import type {DraftIssueProjectReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {disableStreamingFadeIn} from '@github-ui/copilot-markdown'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Spinner} from '@primer/react'
import {loadAll} from 'js-yaml'
import {useCallback, useEffect, useMemo} from 'react'

import {getRevisionNumber, stripVersionFromId} from '../../../components/ContentPreview/content-preview-types'
import {useContentPreview} from '../../../components/ContentPreview/ContentPreviewContext'
import {IssueIcon} from '../../../components/ContentPreview/create-issue-preview/IssueIcon'
import {type BlockedByDependencies, IssueTree} from '../../../components/ContentPreview/create-issue-preview/IssueTree'
import {
  toTreeMap,
  type TreeNode,
  visit,
} from '../../../components/ContentPreview/create-issue-preview/use-draft-issue-tree-map'
import {usePreProcessedDraftIssueContent} from '../../../components/ContentPreview/create-issue-preview/use-pre-processed-new-issue'
import {useContentPreviewBlockContext} from '../../ContentPreviewBlockContext'
import {useIsStreamingComplete} from '../../hooks/use-is-streaming-complete'
import {DraftIssue} from '../draft-issue/DraftIssueBlock'
import {useDraftIssueReferenceValidation} from '../hooks/use-draft-issue-reference-validation'
import {IssueTreeItem} from '../issue-tree/IssueTreeBlock'
import type {DraftIssue as DraftIssueType} from '../types'
import styles from './DraftIssueTreeBlock.module.css'

export interface DraftIssueTreeBlockProps {
  data?: string
  isStreaming?: boolean
}

interface DraftIssueTreeItem extends DraftIssueType {
  state?: 'draft'
}

function isDraftIssueTreeItem(item: TreeItem): item is DraftIssueTreeItem {
  return 'state' in item && item.state === 'draft'
}

interface ExistingIssueTreeItem extends IssueTreeItem {
  state?: 'open' | 'closed'
}

function isExistingIssueTreeItem(item: TreeItem): item is ExistingIssueTreeItem {
  return 'state' in item && (item.state === 'open' || item.state === 'closed')
}

type TreeItem = DraftIssueTreeItem | ExistingIssueTreeItem

function parseListData(yaml: string) {
  try {
    const result = loadAll(yaml)
    if (Array.isArray(result)) {
      return result
        .filter(item => typeof item === 'object' && item != null)
        .map(item => {
          const state = 'state' in item ? item.state : undefined
          switch (state) {
            case undefined:
            case 'draft':
              return DraftIssue.fromUnknown(item) as DraftIssueTreeItem
            case 'open':
            case 'closed':
              return IssueTreeItem.fromUnknown(item) as ExistingIssueTreeItem
          }
        })
        .filter(item => !!item)
        .filter(item => !!item.tag) // Relationships require the tag value to be set
    }
    return null
  } catch {
    return null
  }
}

export function DraftIssueTreeBlock({data, isStreaming}: DraftIssueTreeBlockProps) {
  // pre-process the raw YAML content to transform image URLs etc before parsing
  const processedData = usePreProcessedDraftIssueContent(data)

  // Process YAML data for issues
  const issues = useMemo(() => {
    return parseListData(processedData) || []
  }, [processedData])

  const {items, updateItem, openItem, openPreviewPane, closeItem, versionedItems} = useContentPreview()
  const {messageId, messageIndex, autoOpenPreviewPane, hasAutoOpenedPreviewPaneRef, setHasAutoOpenedPreviewPane} =
    useContentPreviewBlockContext()

  const getId = useCallback(
    (item: TreeItem) => {
      if (isDraftIssueTreeItem(item) && item.tag) {
        const draftIssueType = item.type === 'draft-update-issue' ? 'update-issue' : 'new-issue'
        return `${draftIssueType}:${item.tag}#${messageIndex}` as const
      }
      if (isExistingIssueTreeItem(item) && item.repository && item.number) {
        return `existing-issue:${item.repository}/${item.number}` as const
      }
    },
    [messageIndex],
  )

  // Validate draft issues against references using shared hook
  useDraftIssueReferenceValidation({
    issues,
    isStreaming,
    isIssueTree: true,
  })

  // Update the tree items in the content preview context
  useEffect(() => {
    for (const issue of issues) {
      // We're filtering out items missing `tag` during parsing, but the types don't know that...
      if (!issue.tag) continue

      // Don't update items until we have a valid ID. This also covers when we can't tell yet which type it is.
      const id = getId(issue)
      if (!id) continue

      if (isDraftIssueTreeItem(issue)) {
        const draftIssueType = issue.type === 'draft-update-issue' ? 'update-issue' : 'new-issue'
        const issueId = `${draftIssueType}:${issue.tag}#${messageIndex}` as const

        // Close any existing issue tab when drafting updates
        if (draftIssueType === 'update-issue' && issue.repository && issue.issueNumber) {
          closeItem(`issue:${issue.repository}/${issue.issueNumber}`)
        }

        updateItem({
          messageId,
          type: draftIssueType,
          id: issueId,
          tag: issue.tag,
          parentTag: issue.parentTag,
          name: issue.title ?? '',
          body: issue.body,
          bodyVersion: issue.bodyVersion,
          repository: issue.repository,
          template: issue.template,
          issueType: issue.issueType,
          milestone: issue.milestone,
          assignees: issue.assignees?.sort(),
          labels: issue.labels?.sort(),
          projects: issue.projects
            ?.map(project => {
              // Handle both string format (legacy) and object format (enhanced with IDs)
              if (typeof project === 'object' && project !== null) {
                // Handle enhanced project format with potential ID and title
                const projectObj = project as Record<string, unknown>
                return {
                  id: typeof projectObj.id === 'string' ? projectObj.id : undefined,
                  title: typeof projectObj.title === 'string' ? projectObj.title : undefined,
                } as DraftIssueProjectReference
              } else {
                return {title: String(project)}
              }
            })
            .filter(project => Boolean(project.title) || Boolean(project.id))
            .sort((a, b) => (a.title || '').localeCompare(b.title || '')),
          defaultValuesById: issue.templateFormValues ?? {},
          isUserEdited: false,
          isStreaming,
          blockedByTags: issue.blockedByTags ?? [],
          isBlockingTags: issue.isBlockingTags ?? [],
          issueNumber: issue.issueNumber,
        })
      }
      if (isExistingIssueTreeItem(issue) && issue.repository && issue.number && issue.url) {
        // Because we may be adding on to an issue that was just created, there may
        // already be a tab open for the `issue:*` preview type. Close it.
        closeItem(`issue:${issue.repository}/${issue.number}`)

        updateItem({
          messageId,
          type: 'existing-issue',
          id: `existing-issue:${issue.repository}/${issue.number}` as const,
          tag: issue.tag,
          parentTag: issue.parentTag,
          repository: issue.repository,
          number: issue.number,
          name: issue.title ?? '',
          state: issue.state ?? 'open',
          href: issue.url,
          blockedByTags: issue.blockedByTags ?? [],
          isBlockingTags: issue.isBlockingTags ?? [],
        })
      }
    }
  }, [getId, isStreaming, issues, messageId, messageIndex, updateItem, closeItem])

  const openIssue = useCallback(
    (issue: TreeItem, {selectItem, event}: {selectItem: boolean; event?: React.MouseEvent}) => {
      const id = getId(issue)
      if (id) {
        openItem(id, selectItem)

        if (selectItem || (autoOpenPreviewPane && !hasAutoOpenedPreviewPaneRef.current)) {
          openPreviewPane(event?.currentTarget as HTMLElement)
          setHasAutoOpenedPreviewPane(true)
        }

        sendEvent('dotcom_chat.activate', {
          target: 'BROWSER_DRAFT_ISSUE_OPENED',
          mode: 'immersive',
        })
      }
    },
    [getId, openItem, autoOpenPreviewPane, hasAutoOpenedPreviewPaneRef, openPreviewPane, setHasAutoOpenedPreviewPane],
  )

  const resolveMissingIssue = useCallback(
    (missingTag: string) => {
      // Find the first item with matching tag to get the id
      const missingItem = [...items.entries()].find(
        ([id, item]) =>
          (id.startsWith('new-issue:') || id.startsWith('existing-issue:')) &&
          (item.type === 'new-issue' || item.type === 'existing-issue') &&
          item.tag === missingTag,
      )

      if (!missingItem) return

      const [missingId] = missingItem
      // Get the base id to find the latest version
      const unversionedId = stripVersionFromId(missingId)
      const versions = versionedItems.get(unversionedId)
      const latestVersionId = versions?.at(-1)

      if (!latestVersionId) return

      const latestMissingIssue = items.get(latestVersionId)
      if (!latestMissingIssue) return

      const type = latestMissingIssue?.type
      switch (type) {
        case 'new-issue':
        case 'update-issue': {
          const draftIssueTreeItem = DraftIssue.fromUnknown(latestMissingIssue) as DraftIssueTreeItem
          return {...draftIssueTreeItem, state: 'draft'}
        }
        case 'existing-issue':
          return IssueTreeItem.fromUnknown(latestMissingIssue) as TreeItem
        default:
          return
      }
    },
    [items, versionedItems],
  )

  const rootNodes = useMemo(() => {
    const distinctTags = new Set(issues.map(issue => issue.tag!))
    const distinctParentTags = new Set(issues.filter(x => x.parentTag != null).map(x => x.parentTag))
    const distinctBlockedByTags = new Set(
      issues.flatMap(x => (x.blockedByTags != null ? x.blockedByTags : [])).filter(tag => tag != null),
    )
    const distinctIsBlockingTags = new Set(
      issues.flatMap(x => (x.isBlockingTags != null ? x.isBlockingTags : [])).filter(tag => tag != null),
    )

    // Convert the union of parent/blocked tags to an array, remove undefined and tags that already
    // exist in the current issues set, then resolve missing issues for those tags.
    const missingTags = Array.from(
      new Set([...distinctParentTags, ...distinctBlockedByTags, ...distinctIsBlockingTags]),
    ).filter((tag): tag is string => tag != null && !distinctTags.has(tag))

    const resolvedIssues = missingTags
      .map(tag => resolveMissingIssue(tag))
      .filter((issue): issue is TreeItem => issue != null)

    const issuesWithResolvedParentsAndDependencies = [...issues, ...resolvedIssues]

    const tree = toTreeMap(
      issuesWithResolvedParentsAndDependencies,
      issue => issue.tag!,
      issue => issue.parentTag,
      issue => issue.blockedByTags,
      issue => issue.isBlockingTags,
    )

    return Array.from(tree.values()).filter(node => !node.parent)
  }, [issues, resolveMissingIssue])

  // Auto-open all the top-level issues once streaming is complete
  const isStreamingCompleted = useIsStreamingComplete(isStreaming)

  useEffect(() => {
    if (!isStreamingCompleted) return

    let selectItem = true
    for (const node of rootNodes.values()) {
      // If the root node is a draft, we'll go ahead and open it
      if (isDraftIssueTreeItem(node.item)) {
        openIssue(node.item, {selectItem})
        selectItem = false // only select the first item
        continue
      }

      // Otherwise, if the root is an existing issue, we'll instead open it's newly-drafted children
      for (const child of node.children) {
        if (isDraftIssueTreeItem(child.item)) {
          openIssue(child.item, {selectItem})
          selectItem = false // only select the first item
        }
      }
    }
  }, [isStreamingCompleted, rootNodes, openIssue])

  const blockedByDependencies = (node: TreeNode<TreeItem>): BlockedByDependencies[] | undefined => {
    return node.blockedBy.map(b => {
      return {
        title: b.item.title ?? '',
        href: isExistingIssueTreeItem(b.item) ? b.item.url : undefined,
        onClick: e => {
          if (e.metaKey || e.ctrlKey) return
          e.preventDefault()
          openIssue(b.item, {selectItem: true, event: e})
        },
      }
    })
  }

  const renderNode = (node: TreeNode<TreeItem>) => {
    const id = getId(node.item)
    const version = id ? getRevisionNumber(id, versionedItems, items) : null

    return (
      <IssueTree.Item
        key={node.item.tag}
        title={node.item.title ?? ''}
        leadingVisual={<LeadingVisual isStreaming={isStreaming} item={node.item} />}
        trailingBadge={version != null ? <VersionName version={version} /> : undefined}
        onClick={e => {
          if (e.metaKey || e.ctrlKey) return
          e.preventDefault()
          openIssue(node.item, {selectItem: true, event: e})
        }}
        href={isExistingIssueTreeItem(node.item) ? node.item.url : undefined}
        blockedBy={blockedByDependencies(node)}
      >
        {node.children.map(renderNode)}
      </IssueTree.Item>
    )
  }

  return (
    <div className={styles.container}>
      {rootNodes.map(node => {
        const id = getId(node.item)
        const version = id ? getRevisionNumber(id, versionedItems, items) : null
        let totalChildCount = -1 // start at -1 to not count the current node itself
        visit(node, () => totalChildCount++)

        return (
          <IssueTree
            key={node.item.tag}
            subIssueHeaderText={isExistingIssueTreeItem(node.item) ? 'New sub-issues' : undefined}
            subIssueCount={totalChildCount}
          >
            <IssueTree.RootItem
              title={node.item.title ?? ''}
              leadingVisual={<LeadingVisual isStreaming={isStreaming} item={node.item} />}
              trailingBadge={
                version != null ? <VersionName data-testid="version-label" version={version} /> : undefined
              }
              onClick={e => {
                if (e.metaKey || e.ctrlKey) return
                e.preventDefault()
                openIssue(node.item, {selectItem: true, event: e})
              }}
              href={isExistingIssueTreeItem(node.item) ? node.item.url : undefined}
              blockedBy={blockedByDependencies(node)}
            />
            {node.children.map(renderNode)}
          </IssueTree>
        )
      })}
    </div>
  )
}

function LeadingVisual({isStreaming, item}: {isStreaming: boolean | undefined; item: TreeItem}) {
  if (isStreaming) {
    return <Spinner size="small" className={`${disableStreamingFadeIn} mt-1`} />
  }
  if (isDraftIssueTreeItem(item)) {
    const draftIssueType = item.type === 'draft-update-issue' ? 'update-issue' : 'new-issue'
    return <IssueIcon type={draftIssueType} />
  }
  if (isExistingIssueTreeItem(item)) {
    return <IssueIcon type={'existing-issue'} state={item.state!} />
  }
}
